import React from 'react';
import { useSyncContext } from '@/features/sync/context/SyncProvider';
import { Card } from './ui/card';

interface SyncProgressBarProps {
  progress: number;
  label?: string;
}

const SyncProgressBar: React.FC<SyncProgressBarProps> = ({ progress, label }) => {
  return (
    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
      <div
        className="h-full bg-green-500 transition-all duration-300 ease-in-out"
        style={{ width: `${String(progress)}%` }}
      />
      {label && <div className="text-xs text-gray-600 mt-1">{label}</div>}
    </div>
  );
};

export const SyncProgress: React.FC = () => {
  const { syncProgress } = useSyncContext();

  if (!syncProgress) {
    return null;
  }

  // Don't show if sync is complete or offline
  if (syncProgress.status === 'complete' || syncProgress.status === 'offline') {
    return null;
  }

  return (
    <Card className="fixed bottom-4 right-4 p-4 rounded-lg shadow-xs w-80 z-50 hue:bg-secondary">
      <div className="flex flex-col gap-3">
        <div className="text-sm font-semibold">Syncing data... ({syncProgress.status})</div>

        {/* Overall progress */}
        <div className="space-y-1">
          <div className="flex justify-between text-xs ">
            <span>Overall progress</span>
            <span>{syncProgress.overallCompletionPercentage}%</span>
          </div>
          <SyncProgressBar progress={syncProgress.overallCompletionPercentage} />
        </div>

        {/* Collections progress - show always for debugging */}
        <div className="space-y-1">
          <div className="flex justify-between text-xs ">
            <span>
              Collections ({syncProgress.collectionsProcessed}/
              {syncProgress.totalEstimatedCollections || '?'})
            </span>
            <span>{syncProgress.collectionsCompletionPercentage}%</span>
          </div>
          <SyncProgressBar progress={syncProgress.collectionsCompletionPercentage} />
        </div>

        {/* Pages progress - show always for debugging */}
        <div className="space-y-1">
          <div className="flex justify-between text-xs ">
            <span>
              Pages ({syncProgress.pagesProcessed}/{syncProgress.totalEstimatedPages || '?'})
            </span>
            <span>{syncProgress.pagesCompletionPercentage}%</span>
          </div>
          <SyncProgressBar progress={syncProgress.pagesCompletionPercentage} />
        </div>

        {/* Current entity being processed */}
        <div className="text-xs italic">{syncProgress.currentEntityBeingProcessed}</div>
      </div>
    </Card>
  );
};
